import api, { longRunningApi } from './api';
import { EntityDefinition, FormData } from '@/types/crud';

export class GenericCRUDService {
  protected entity: EntityDefinition;

  constructor(entity: EntityDefinition) {
    this.entity = entity;
  }

  // Get all items
  async getAll(params?: {
    search?: string;
    orderBy?: string;
    orderDirection?: string;
    page?: number;
    limit?: number;
    [key: string]: unknown;
  }): Promise<any> {
    const response = await api.get(this.entity.apiEndpoint, { params });
    
    // Handle unified pagination response format
    if (response.data.pagination) {
      const pagination = response.data.pagination;
      
      const result = {
        data: response.data.data,
        pagination: {
          page: pagination.page || 1,
          limit: pagination.limit || 10,
          total: pagination.total || 0,
          totalPages: pagination.totalPages || 1,
          hasNext: pagination.hasNext || false,
          hasPrev: pagination.hasPrev || false
        }
      };
      
      return result;
    }
    
    // Fallback to direct format - check if pagination data exists in response
    if (response.data.total !== undefined) {
      return {
        data: response.data.data || response.data,
        pagination: {
          page: response.data.currentPage || 1,
          limit: response.data.itemsPerPage || 10,
          total: response.data.total || 0,
          totalPages: response.data.totalPages || 1,
          hasNext: response.data.hasNext || false,
          hasPrev: response.data.hasPrev || false
        }
      };
    }
    
    // Final fallback
    return response.data;
  }

  // Get single item
  async getById(id: string): Promise<any> {
    const response = await api.get(`${this.entity.apiEndpoint}/${id}`);
    // Handle both response structures: { data: {...} } and direct data
    return response.data.data || response.data;
  }

  // Create new item
  async create(data: FormData): Promise<any> {
    const response = await api.post(this.entity.apiEndpoint, data);
    return response.data;
  }

  // Update item
  async update(id: string, data: FormData): Promise<any> {
    const response = await api.put(`${this.entity.apiEndpoint}/${id}`, data);
    return response.data;
  }

  // Delete item
  async delete(id: string): Promise<void> {
    try {
      // Use longRunningApi for delete operations to allow more time
      const response = await longRunningApi.delete(`${this.entity.apiEndpoint}/${id}`);
      
      // If the response contains logs, print them to the browser console
      if (response.data?.logs && Array.isArray(response.data.logs)) {
        console.group(`🗑️ [Delete ${this.entity.name}] Deletion Logs for ID: ${id}`);
        response.data.logs.forEach((log: string) => {
          console.log(log);
        });
        console.groupEnd();
      }
    } catch (error: any) {
      // Also log deletion logs from error responses
      if (error.response?.data?.logs && Array.isArray(error.response.data.logs)) {
        console.group(`❌ [Delete ${this.entity.name}] Deletion Error Logs for ID: ${id}`);
        error.response.data.logs.forEach((log: string) => {
          console.log(log);
        });
        console.groupEnd();
      }
      // Log timeout errors specifically
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        console.error(`⏱️ [Delete ${this.entity.name}] Request timed out after 2 minutes. The deletion may still be processing on the server.`);
        if (error.response?.data?.logs && Array.isArray(error.response.data.logs)) {
          console.group(`⚠️ [Delete ${this.entity.name}] Partial Logs (before timeout):`);
          error.response.data.logs.forEach((log: string) => {
            console.log(log);
          });
          console.groupEnd();
        }
      }
      throw error;
    }
  }

  // Copy item with " - Copy" suffix - using dedicated copy endpoint
  async copy(id: string): Promise<any> {
    const response = await api.post(`${this.entity.apiEndpoint}/${id}/copy`);
    return response.data;
  }

  // Toggle status (for entities that support status changes)
  async toggleStatus(id: string, isActive: boolean): Promise<any> {
    // For ABAC entities, use is_active field
    if (this.entity.apiEndpoint.includes('/abac_')) {
      const response = await api.patch(`${this.entity.apiEndpoint}/${id}/status`, { is_active: isActive });
      return response.data;
    }
    // KPI catalog uses ACTIVE / INACTIVE on the status column
    if (this.entity.apiEndpoint.includes('/kpis') || this.entity.name === 'kpi') {
      const response = await api.patch(`${this.entity.apiEndpoint}/${id}/status`, { is_active: isActive });
      return response.data;
    }
    // For other entities, use status field
    const status = isActive ? 'ACTIVE' : 'DRAFT';
    const response = await api.patch(`${this.entity.apiEndpoint}/${id}/status`, { status });
    return response.data;
  }

  // Bulk operations
  async bulkDelete(ids: string[]): Promise<void> {
    await api.post(`${this.entity.apiEndpoint}/bulk-delete`, { ids });
  }

  async bulkUpdate(ids: string[], data: Partial<FormData>): Promise<void> {
    await api.put(`${this.entity.apiEndpoint}/bulk-update`, { ids, data });
  }
}

// Factory function to create CRUD service for any entity
export function createCRUDService(entity: EntityDefinition): GenericCRUDService {
  return new GenericCRUDService(entity);
} 